import { useContext, useEffect, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Input from "@mui/material/Input";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import HtmlEditor from "../../HtmlEditor/HtmlEditor";
import ButtonBase from "@mui/material/ButtonBase";
import { blobToData } from "../../../../helpers/data";
import { toast } from "react-toastify";
import AuthContext from "../../../../store/auth-contex";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../Button/Button";

const generateBootstrapClasses = (columns) => {
    if (columns) {
        const columnClasses = [];
        Object.keys(columns).forEach((screenSize) => {
            const columnValue = columns[screenSize];
            if (columnValue) {
                columnClasses.push(`col-${screenSize}-${columnValue}`);
            }
        });
        return columnClasses.join(" ");
    }
    return "";
};

/**
 * Wrapper for the input element
 *
 * @param {JSX.Element} children
 * @param {string} label Field label
 * @param {string} error Error message
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 *
 * @return {JSX.Element}
 */

export const InputWrapper = ({ children = null, label, required, disabled, margin = "dense", error = null, fullWidth = true, styleFormControl, inputClasses }) => {
    return (
        <FormControl fullWidth={fullWidth} margin={margin} error={error !== null} sx={{ padding: "0 0.3rem", ...styleFormControl }} className={inputClasses}>
            <FormLabel required={required} disabled={disabled}>
                {label}
            </FormLabel>
            {children}
        </FormControl>
    );
};

/**
 * Basic text input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {boolean} autoFocus True to allow this input to capture focus.
 * @param {string} type The type of the input.
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 * @param {boolean} readOnly If input is read only
 *
 * @return {JSX.Element}
 */

export const InputInput = ({ label, required, disabled, name, value, autoFocus, type = "text", error = null, margin = "dense", onChange = () => null, description, placeholder, readOnly, uiProp }) => {
    const existingStyles = {
        "& legend": { display: "none" },
        "& fieldset": { top: 0 },
        ".MuiInputBase-root": { overflow: "hidden" },
        ".MuiInputBase-input": { padding: "0.7rem", fontSize: "0.875rem" },
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper
            label={uiProp?.form?.input?.label === false ? null : label}
            required={required}
            disabled={disabled}
            margin={margin}
            error={error}
            styleFormControl={uiProp?.wrapper_props?.custom_sx}
            inputClasses={inputClasses}
        >
            <TextField
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                autoFocus={autoFocus}
                helperText={error ? error : description}
                error={error !== null}
                sx={{
                    ...existingStyles,
                    ...uiProp?.component_props?.custom_sx,
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic number input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputNumber = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, placeholder, autoFocus, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <TextField
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                helperText={error ? error : description}
                error={error !== null}
                autoFocus={autoFocus}
                type="number"
                inputProps={{ min: 0, step: "any" }}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-input": { padding: "0.8rem", fontSize: "0.875rem" },
                }}
                onWheel={(e) => e.target.blur()}
                onFocus={(event) => {
                    event.target.select();
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic checkbox input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputCheckbox = ({
    label,
    required,
    disabled,
    name,
    value,
    error = null,
    margin = "dense",
    onChange = () => null,
    description,
    labelStyle,
    styleCheckbox,
    styleCheckBoxWrapp,
    uiProp,
    id,
}) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleCheckBoxWrapp} inputClasses={inputClasses}>
            <FormControlLabel
                control={<Checkbox name={name} id={id} checked={value} onChange={onChange} disabled={disabled} sx={styleCheckbox} />}
                label={label}
                sx={{ ".MuiTypography-root": { fontSize: "14px" }, ...labelStyle }}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic radio input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputRadio = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <FormControlLabel sx={{ ".MuiTypography-root": { fontSize: "0.875rem" } }} control={<Radio name={name} checked={value} onChange={onChange} disabled={disabled} />} label={label} />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic switch input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {boolean} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputSwitch = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, fullWidth = true, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} fullWidth={fullWidth} inputClasses={inputClasses}>
            <FormControlLabel
                sx={{ ".MuiTypography-root": { fontSize: "0.875rem" } }}
                control={<Switch name={name} value={value} checked={value} onChange={onChange} disabled={disabled} />}
                label={label}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */

export const InputSelect = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => null,
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    className,
    onDataReceived = () => null,
    styleFormControl,
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        let res = response?.payload;
                        setOpt(res);
                        onDataReceived(res);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    useEffect(() => {
        setOpt(opt?.length > 0 ? opt : options);
    }, [options, opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleFormControl} inputClasses={inputClasses}>
            <Select
                className={className}
                name={name}
                value={(opt ?? []).length === 0 ? "" : value}
                onChange={onChange}
                disabled={disabled}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiSelect-select": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            >
                {(opt ?? []).map((item) => (
                    <MenuItem sx={{ fontSize: "0.875rem" }} key={item.id} value={item?.id} selected={item.id === value} disabled={item?.disabled ?? false} props={item.props} valuename={item?.name}>
                        {item?.name}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */

export const AutocompleteInput = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);
    const [myValue, setMyValue] = useState(null);

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        setOpt(response?.payload?.filter((item) => item !== null));
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    useEffect(() => {
        let selectedOption = null;
        if (opt.length > 0) {
            selectedOption = opt.find((o) => o.id === value)?.name;
            if (selectedOption === undefined) {
                selectedOption = null;
            }
            setMyValue(selectedOption);
        }
    }, [opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <Autocomplete
                value={myValue}
                onInputChange={(event, newInputValue) => {
                    let newIval = newInputValue ? newInputValue : "";
                    setMyValue(newIval);
                    if (opt.length > 0) {
                        let selectedOption = opt.find((o) => o.name === newInputValue);
                        if (selectedOption) {
                            newIval = selectedOption.id;
                        }
                    }
                    onChange(name, newIval);
                }}
                options={opt.map((option) => option?.name)}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0.23rem !important" },
                }}
                renderInput={(params) => <TextField {...params} />}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const AutocompleteInputFreeSolo = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState([]);
    const filter = createFilterOptions();

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path, false)
                .then((response) => {
                    if (isMounted) {
                        const { payload } = response;
                        setOpt(payload);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <Autocomplete
                disabled={disabled}
                value={value}
                onChange={(event, newInputValue) => {
                    let newIval = newInputValue ? newInputValue : "";
                    if (opt.length > 0) {
                        let selectedOption = opt.find((o) => o.name === newInputValue);
                        if (selectedOption) {
                            newIval = selectedOption.id;
                        }
                    }
                    onChange(name, newIval);
                }}
                options={options?.length > 0 ? options : opt}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.name);
                    if (inputValue !== "" && !isExisting) {
                        filtered.push({
                            inputValue,
                            name: `Dodaj: "${inputValue}"`,
                        });
                    }
                    return filtered;
                }}
                getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                        return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                }}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0 !important" },
                    ".MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "0.7rem !important", fontSize: "0.875rem" },
                }}
                renderInput={(params) => <TextField {...params} />}
                freeSolo={true}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */
// Component that allows you to select more values, as well as add new values
export const AutocompleteTagsFilled = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,      // e.g. "admin/news-b2c/news/basic-data/ddl"
    usePropName,      // usually true → makes /ddl/tag
    options,
    queryString = "",
    uiProp,
}) => {
    const { api } = useContext(AuthContext);

    const [opt, setOpt] = useState(options || []);
    const [myValue, setMyValue] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const filter = createFilterOptions();
    const allowFreeSolo = uiProp?.allowFreeSolo ?? true;
    const allowFillFromApiSearch = uiProp?.allowFillFromApiSearch ?? false;
    
    // --- 1️⃣ Load all existing tags initially ---
    useEffect(() => {
        if (!fillFromApi) return;
        let isMounted = true;

        // always call /ddl/tag for initial load
        const path = usePropName
            ? `${fillFromApi}/${name}?${queryString}` // → /ddl/tag
            : `${fillFromApi}?${queryString}`;

        api.get(path)
            .then((response) => {
                if (!isMounted) return;
                const data = response?.payload ?? [];
                setOpt(data);
            })
            .catch(console.warn);

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, name, queryString, usePropName, api]);

    // --- 2️⃣ Map backend value (IDs or object) into display names ---
    useEffect(() => {
        if (!opt?.length || !value) return;

        const selectedOptions = [];

        // handle { exist: [], new: [] }
        if (typeof value === "object" && Array.isArray(value.exist)) {
            value.exist.forEach((id) => {
                const found = opt.find((o) => o.id === id);
                if (found) selectedOptions.push(found.name);
            });
            if (Array.isArray(value.new)) {
                selectedOptions.push(...value.new);
            }
        }
        // handle [1,6,7]
        else if (Array.isArray(value)) {
            value.forEach((id) => {
                const found = opt.find((o) => o.id === id);
                if (found) selectedOptions.push(found.name);
            });
        }

        setMyValue(selectedOptions);
    }, [opt, value]);

    // --- 3️⃣ If some IDs returned from API aren’t yet in opt, refetch them from SAME endpoint /ddl/tag?ids=... ---
    useEffect(() => {
        if (!Array.isArray(value) || !fillFromApi || !value.length) return;

        // find IDs missing in current opt
        const missingIds = value.filter((id) => !opt.some((o) => o.id === id));
        if (!missingIds.length) return;

        const fetchMissing = async () => {
            try {
                const basePath = usePropName
                    ? `${fillFromApi}/${name}`
                    : `${fillFromApi}`;
                const url = `${basePath}?ids=${missingIds.join(",")}`;

                const resp = await api.get(url);
                const newData = resp?.payload ?? [];

                // merge missing tags
                setOpt((prev) => [
                    ...prev,
                    ...newData.filter((n) => !prev.some((p) => p.id === n.id)),
                ]);
            } catch (err) {
                console.warn("Failed to load missing tags:", err);
            }
        };

        fetchMissing();
    }, [value, fillFromApi, name, usePropName]);

    useEffect(() => {
        if (!fillFromApi) return;
        if (!inputValue) return;
        if (!allowFillFromApiSearch) return;

        const delay = setTimeout(() => {
            const path = usePropName
                ? `${fillFromApi}/${name}?search_term=${inputValue}`
                : `${fillFromApi}?search_term=${inputValue}`;

            api.get(path)
                .then((res) => {
                    setOpt(res?.payload ?? []);
                })
                .catch(console.warn);
        }, 300); // debounce

        return () => clearTimeout(delay);
    }, [inputValue]);

    // --- 4️⃣ Handle user selecting or creating new tags ---
    const handleChange = (event, newValue) => {
        setInputValue("");

        const sanitizedValue = allowFreeSolo
            ? newValue
            : newValue.filter((item) =>
                opt.some((o) => o.name === item)
            );
            
        setMyValue(sanitizedValue);

        const for_save = { exist: [], new: [] };

        sanitizedValue.forEach((item) => {
            const found = opt.find((o) => o.name === item);
            if (found) for_save.exist.push(found.id);
            else for_save.new.push(item);
        });

        onChange(name, for_save);
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper
            label={label}
            required={required}
            disabled={disabled}
            margin={margin}
            error={error}
            inputClasses={inputClasses}
        >
            <Autocomplete
                multiple
                freeSolo={allowFreeSolo}
                filterOptions={(optionsList, params) => {
                    const filtered = filter(optionsList, params);
                    if (!allowFreeSolo) {
                        return filtered;
                    }
                    const { inputValue } = params;
                    const exists = optionsList.some((optItem) => inputValue === optItem);
                    if (inputValue !== "" && !exists) {
                        filtered.push(inputValue);
                    }
                    return filtered;
                }}
                value={myValue}
                inputValue={inputValue}
                onInputChange={(e, v) => setInputValue(v)}
                onChange={handleChange}
                options={opt.map((option) => option.name)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            key={index}
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params) => <TextField {...params} label={label} />}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiFormLabel-root": { display: "none" },
                    ".MuiInputBase-input": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            />
            <FormHelperText>{error || description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 * @param {boolean} selectAllEnabled Determines if select and deselect all buttons are displayed or not
 *
 * @return {JSX.Element}
 */

export const InputMultiSelect = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => null,
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    styleMultiSelect,
    uiProp,
    selectAllEnabled = false,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);
    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        setOpt(response?.payload);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi]);

    useEffect(() => {
        if (options && options?.length > 0) {
            setOpt(options);
        } else {
            setOpt(opt);
        }
    }, [options, opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleMultiSelect} inputClasses={inputClasses}>
            <Select
                name={name}
                value={(opt ?? []).length === 0 ? [] : value}
                onChange={onChange}
                disabled={disabled}
                multiple={true}
                renderValue={(selected) => {
                    let display = [];
                    for (const option of opt) {
                        if (selected.includes(option.id)) {
                            display.push(option.name);
                        }
                    }
                    return display.join(", ");
                }}
                sx={{
                    position: "relative",
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiSelect-select": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            >
                {selectAllEnabled && (
                    <Box
                        sx={{ display: "flex", gap: "0.7rem", padding: "0.7rem", position: "sticky", top: 0, background: "white", opacity: 1, zIndex: 20 }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Button
                            label="Izaberi sve"
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange({ target: { value: opt.map((item) => item.id), name } });
                            }}
                        />
                        <Button
                            label="Poništi sve"
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange({ target: { value: [], name } });
                            }}
                        />
                    </Box>
                )}
                {(opt ?? []).map((item) => (
                    <MenuItem sx={{ fontSize: "0.875rem" }} key={item.id} value={item.id} selected={item.id === value} disabled={item?.disabled ?? false}>
                        <ListItemIcon>
                            <Checkbox checked={value?.indexOf(item.id) > -1} />
                        </ListItemIcon>

                        {item.name}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic textarea input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputText = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, placeholder, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <TextField
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                multiline
                minRows={3}
                helperText={error ? error : description}
                error={error !== null}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0.7rem" },
                    ".MuiInputBase-input": { fontSize: "0.875rem" },
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic datetime input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputDateTime = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, uiProp }) => {
    const handleChange = (newValue) => {
        const ev = {
            target: {
                name: name,
                value: newValue,
            },
        };
        onChange(ev, "date_time");
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    let minDateTime = uiProp?.minDateTime ?? null;
    if(minDateTime == 'now()') {
        minDateTime = new Date();
    }

    console.log({
        "action": "InputDateTime",
        "minDateTime": minDateTime
    });

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                    value={value !== "" ? value : null}
                    onChange={handleChange}
                    ampm={false}
                    showToolbar
                    disabled={disabled}
                    inputFormat="dd/MM/yyyy HH:mm"
                    minDateTime={minDateTime}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{
                                "& legend": { display: "none" },
                                "& fieldset": { top: 0 },
                                ".MuiInputBase-input": { fontSize: "0.875rem", padding: "0.7rem" },
                            }}
                        />
                    )}
                />
            </LocalizationProvider>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic date input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputDate = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, uiProp }) => {
    const handleChange = (newValue) => {
        const ev = {
            target: {
                name: name,
                value: newValue,
            },
        };
        onChange(ev, "date");
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={value !== "" ? value : null}
                    onChange={handleChange}
                    ampm={false}
                    showToolbar
                    disabled={disabled}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{
                                "& legend": { display: "none" },
                                "& fieldset": { top: 0 },
                                ".MuiInputBase-input": { fontSize: "0.875rem", padding: "0.7rem" },
                            }}
                        />
                    )}
                />
            </LocalizationProvider>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Input that returns html
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputHtml = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <HtmlEditor name={name} value={value} onChange={onChange} />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const ImportPicker = ({ label, required, disabled, margin, error = null, onFilePicked, description, selectedFile, uiProp }) => {
    const ref = useRef();

    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        const [file] = files;

        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (fileExtension !== "csv" && fileExtension !== "xml" && fileExtension !== "json") {
            toast.error("Pogrešan tip fajla.");
            return;
        }

        blobToData(file).then((result) => {
            let obj = {
                base_64: result,
                name: file?.name,
            };
            onFilePicked(obj);
        });
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <ButtonBase
                component="label"
                sx={{
                    height: "2.844rem",
                    border: "1px solid",
                    borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                    borderRadius: "0.25rem",
                    justifyContent: "start",
                    paddingLeft: "0.875rem",
                }}
            >
                {selectedFile ? (
                    <span style={{ fontSize: "0.875rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Izabrani fajl: {selectedFile.name}</span>
                ) : (
                    <span style={{ fontSize: "0.875rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Kliknite ovde kako biste odabrali fajl za import.</span>
                )}
                <Input type="file" onChange={handleChange} inputRef={ref} disabled={disabled} sx={{ display: "none" }} error={error !== null} />
            </ButtonBase>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const FilePicker = ({
    label,
    required,
    disabled,
    margin,
    value,
    error = null,
    onFilePicked = () => {},
    description,
    selectedFile,
    uiProp,
    multipleFileSelection = false,
    handleRemoveFile = () => {},
    onChange,
    name,
}) => {
    const ref = useRef();
    const [filename, setFilename] = useState("");
    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        const [file] = files;

        blobToData(file);

        const reader = new FileReader();
        reader.onload = (e) => {
            const base64 = e.target.result;
            const obj = {
                base_64: base64,
                file_name: file?.name,
                file: file,
                name,
            };

            setFilename(file.name);
            onFilePicked(obj);
            onChange({ target: obj }, "file_picker");
        };
        reader.readAsDataURL(file);
    };

    const getFileTypeLabel = (value) => {
        if (!value.startsWith("data:")) return value;

        const mimeType = value.split(";")[0].split(":")[1];

        if (!mimeType) return "Base64 fajl";

        if (mimeType.startsWith("video/")) return "Base64 video";
        if (mimeType.startsWith("image/")) return "Base64 slika";
        if (mimeType.startsWith("audio/")) return "Base64 audio";
        if (mimeType === "application/pdf") return "Base64 PDF";
        if (mimeType === "application/json") return "Base64 JSON";

        return "Base64 fajl";
    };

    const renderSelectedFiles = () => {
        if (multipleFileSelection) {
            return (
                selectedFile && (
                    <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial", display: "flex", width: "100%" }}>
                        Izabrani fajlovi:
                        {selectedFile.length > 0
                            ? selectedFile.map((file, index) => {
                                  return (
                                      <Box
                                          key={file.name}
                                          sx={{
                                              marginLeft: index === 0 ? "0.3rem" : "0",
                                              whiteSpace: "wrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                          }}
                                      >
                                          {index > 0 && ", "}
                                          {file.name}
                                      </Box>
                                  );
                              })
                            : " Kliknite ovde kako biste odabrali fajl."}
                    </span>
                )
            );
        } else {
            return filename || value ? (
                <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Izabrani fajl: {filename || getFileTypeLabel(value)}</span>
            ) : (
                <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Kliknite ovde kako biste odabrali fajl.</span>
            );
        }
    };

    const renderRemoveFilesList = () => {
        if (multipleFileSelection) {
            return selectedFile?.map((item) => (
                <span
                    span
                    key={item?.name}
                    style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial", display: "flex", alignItems: "center", marginRight: "0.8rem" }}
                >
                    {item?.name}
                    <CloseIcon
                        sx={{ fontSize: "0.9rem", cursor: "pointer", marginLeft: "0.2rem" }}
                        onClick={() => {
                            handleRemoveFile(item);
                        }}
                    />
                </span>
            ));
        }
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);
    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <ButtonBase
                component="label"
                sx={{
                    height: "2.844rem",
                    border: "1px solid",
                    borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                    borderRadius: "0.25rem",
                    justifyContent: "start",
                    paddingLeft: "0.875rem",
                }}
            >
                <Input
                    type="file"
                    onChange={handleChange}
                    inputRef={ref}
                    disabled={disabled}
                    error={error !== null}
                    sx={{ display: "none" }}
                    multiple
                    inputProps={{
                        accept: uiProp?.fileUpload?.allow_format?.map((format) => format?.mime_type),
                    }}
                />
                {renderSelectedFiles()}
            </ButtonBase>
            <Box sx={{ display: "flex" }}>{renderRemoveFilesList()}</Box>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};
